// Asset Accountability & Network Discovery — hardware lifecycle, separation
// holds, refresh forecasting, and passive observation of network-observed devices

import { pbAuthedFetch } from '../authHeaders';
import { useQuery } from '@tanstack/react-query';
import { useState, useMemo } from 'react';
import { StalenessIndicator } from '../components/provenance/StalenessIndicator';
import { useTableControls, type Column } from '../hooks/useTableControls';
import { DataTable } from '../components/DataTable';

/* ── assets panel ────────────────────────────────────────── */

function AssetsPanel() {
  const { data: assetsData, isLoading } = useQuery({
    queryKey: ['assets'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/assets');
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
  });

  const { data: separationData } = useQuery({
    queryKey: ['assets-separation-holds'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/assets/separation-holds');
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
  });

  const { data: checkinData } = useQuery({
    queryKey: ['assets-checkin-compliance'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/assets/checkin-compliance');
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
  });

  const { data: forecastData } = useQuery({
    queryKey: ['assets-refresh-forecast'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/assets/refresh-forecast');
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
  });

  const assets = assetsData?.assets || [];
  const separationHolds = separationData?.holds || [];
  const checkin = checkinData?.summary || {};
  const forecast = forecastData?.forecast || {};

  const pastEOL = forecast.past_eol || [];
  const byFY = forecast.by_fiscal_year || [];

  // --- Separation Holds columns & controls ---
  const separationColumns: Column<any>[] = [
    { key: 'asset_tag', label: 'Asset Tag', render: (r) => <span style={{ fontWeight: 500 }}>{r.asset_tag}</span> },
    { key: 'serial', label: 'Serial', render: (r) => <span style={{ fontFamily: 'var(--mono)', fontSize: 'var(--text-sm)' }}>{r.serial || '—'}</span> },
    { key: 'owner_account', label: 'Owner', render: (r) => <span style={{ fontFamily: 'var(--mono)', fontSize: 'var(--text-sm)' }}>{r.owner_account || '—'}</span> },
    { key: 'owner_status', label: 'Owner Status', render: (r) => (
      <span className={`pill pill--${r.owner_status === 'terminated' ? 'risk' : 'warn'}`}>
        <i></i>
        {r.owner_status || 'disabled'}
      </span>
    )},
    { key: 'days_since_separation', label: 'Days Since Separation', render: (r) => (
      <span style={{ fontFamily: 'var(--mono)', fontWeight: 600, color: r.days_since_separation > 30 ? 'var(--risk)' : 'var(--gold)' }}>
        {r.days_since_separation ?? '—'}d
      </span>
    )},
    { key: 'action_required', label: 'Action Required', render: (r) => (
      <span className={`pill pill--${r.days_since_separation > 90 ? 'risk' : r.days_since_separation > 30 ? 'warn' : 'ok'}`}>
        <i></i>
        {r.days_since_separation > 90 ? 'Overdue' : r.days_since_separation > 30 ? 'Due' : 'Pending'}
      </span>
    )},
  ];
  const separationCtrl = useTableControls(separationHolds, separationColumns, { storageKey: 'assets-separation-holds' });

  // --- Asset Inventory columns & controls ---
  const assetColumns: Column<any>[] = [
    { key: 'tag', label: 'Tag', accessor: (r) => r.tag || r.asset_tag, render: (r) => <span style={{ fontWeight: 500 }}>{r.tag || r.asset_tag}</span> },
    { key: 'serial', label: 'Serial', render: (r) => <span style={{ fontFamily: 'var(--mono)', fontSize: 'var(--text-sm)' }}>{r.serial || '—'}</span> },
    { key: 'model', label: 'Model', render: (r) => r.model || '—' },
    { key: 'assigned_account', label: 'Assigned To', render: (r) => <span style={{ fontFamily: 'var(--mono)', fontSize: 'var(--text-sm)' }}>{r.assigned_account || '—'}</span> },
    { key: 'location', label: 'Location', render: (r) => <span style={{ fontSize: 'var(--text-sm)' }}>{r.location || '—'}</span> },
    { key: 'encryption_status', label: 'Encryption', render: (r) => (
      <span className={`pill pill--${r.encryption_status === 'encrypted' ? 'ok' : r.encryption_status === 'unknown' ? 'warn' : 'risk'}`}>
        <i></i>
        {r.encryption_status || 'unknown'}
      </span>
    ), filterOptions: [
      { value: 'encrypted', label: 'Encrypted' },
      { value: 'unknown', label: 'Unknown' },
      { value: 'unencrypted', label: 'Unencrypted' },
    ]},
    { key: 'last_checkin_at', label: 'Last Check-in', dateField: true, render: (r) => r.last_checkin_at ? <StalenessIndicator lastSeenAt={r.last_checkin_at} /> : <span style={{ color: 'var(--ink-45)' }}>Never</span> },
    { key: 'warranty_end', label: 'Warranty End', dateField: true, render: (r) => <span style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-45)' }}>{r.warranty_end ? new Date(r.warranty_end).toLocaleDateString() : '—'}</span> },
  ];
  const assetCtrl = useTableControls(assets, assetColumns, { storageKey: 'assets-inventory' });

  // --- Past EOL columns & controls ---
  const pastEOLColumns: Column<any>[] = [
    { key: 'tag', label: 'Tag', accessor: (r) => r.tag || r.asset_tag, render: (r) => <span style={{ fontFamily: 'var(--mono)' }}>{r.tag || r.asset_tag}</span> },
    { key: 'model', label: 'Model', render: (r) => r.model || '—' },
    { key: 'eol_date', label: 'EOL Date', render: (r) => <span style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-45)' }}>{r.eol_date?.value ? new Date(r.eol_date.value).toLocaleDateString() : '—'}</span> },
    { key: 'days_past_eol', label: 'Days Past EOL', render: (r) => <span style={{ fontFamily: 'var(--mono)', color: 'var(--risk)', fontWeight: 600 }}>{r.days_past_eol ?? '—'}d</span> },
    { key: 'replacement_cost', label: 'Est. Replacement', render: (r) => <span style={{ fontFamily: 'var(--mono)' }}>{r.replacement_cost != null ? `$${r.replacement_cost.toLocaleString()}` : '—'}</span> },
  ];
  const pastEOLCtrl = useTableControls(pastEOL, pastEOLColumns, { storageKey: 'assets-past-eol' });

  // --- By FY columns & controls ---
  const byFYColumns: Column<any>[] = [
    { key: 'fiscal_year', label: 'Fiscal Year', render: (r) => <span style={{ fontWeight: 600 }}>FY{r.fiscal_year}</span> },
    { key: 'asset_count', label: 'Assets Due', render: (r) => <span style={{ fontFamily: 'var(--mono)' }}>{r.asset_count}</span> },
    { key: 'projected_cost', label: 'Projected Cost', render: (r) => <span style={{ fontFamily: 'var(--mono)', fontWeight: 600 }}>${r.projected_cost?.toLocaleString() ?? '—'}</span> },
    { key: 'status', label: 'Status', render: (r) => (
      <span className={`pill pill--${r.status === 'budgeted' ? 'ok' : r.status === 'unfunded' ? 'risk' : 'warn'}`}>
        <i></i>
        {r.status || 'planned'}
      </span>
    )},
  ];
  const byFYCtrl = useTableControls(byFY, byFYColumns, { storageKey: 'assets-refresh-forecast' });

  if (isLoading) {
    return <div className="page-loading" role="status" aria-live="polite">Loading asset data…</div>;
  }

  return (
    <>
      {/* Completeness / notice statement */}
      <div className="notice notice--info">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="16" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
        <div>
          <p className="notice__title">Asset Coverage</p>
          <p className="notice__body">
            ATLAS tracks <strong>{assets.length} asset{assets.length !== 1 ? 's' : ''}</strong>.
            {separationHolds.length > 0 && (
              <> <strong style={{ color: 'var(--risk)' }}>{separationHolds.length} separation hold{separationHolds.length !== 1 ? 's' : ''}</strong> — assets with disabled or terminated owners.</>
            )}
            {pastEOL.length > 0 && (
              <> <strong>{pastEOL.length} asset{pastEOL.length !== 1 ? 's' : ''}</strong> past end-of-life.</>
            )}
          </p>
        </div>
      </div>

      {/* Check-in Compliance — indicator cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 'var(--sp-4)' }} role="group" aria-label="Check-in compliance">
        <div className="ind ind--watch">
          <div className="ind__head">
            <p className="ind__abbr">≤ 30d</p>
            <p className="ind__full">Within 30 Days</p>
          </div>
          <div className="ind__value">
            <p className="ind__v">{checkin.d_0_30 ?? '—'}</p>
          </div>
          <p className="ind__reading">
            <i style={{ background: 'var(--ok)' }}></i>
            <b style={{ color: 'var(--ok)' }}>Compliant</b> — checked in recently
          </p>
        </div>

        <div className="ind ind--watch">
          <div className="ind__head">
            <p className="ind__abbr">30–60d</p>
            <p className="ind__full">30 to 60 Days</p>
          </div>
          <div className="ind__value">
            <p className="ind__v">{checkin.d_30_60 ?? '—'}</p>
          </div>
          <p className="ind__reading">
            <i style={{ background: 'var(--gold)' }}></i>
            <b style={{ color: 'var(--gold)' }}>Warning</b> — approaching overdue
          </p>
        </div>

        <div className="ind ind--breach">
          <div className="ind__head">
            <p className="ind__abbr">60–90d</p>
            <p className="ind__full">60 to 90 Days</p>
          </div>
          <div className="ind__value">
            <p className="ind__v">{checkin.d_60_90 ?? '—'}</p>
          </div>
          <p className="ind__reading">
            <i></i>
            <b>Overdue</b> — follow up required
          </p>
        </div>

        <div className="ind ind--breach">
          <div className="ind__head">
            <p className="ind__abbr">180+d</p>
            <p className="ind__full">Over 180 Days</p>
          </div>
          <div className="ind__value">
            <p className="ind__v">{checkin.d_180_plus ?? '—'}</p>
          </div>
          <p className="ind__reading">
            <i></i>
            <b>Critical</b> — asset may be lost
          </p>
        </div>
      </div>

      {/* Separation Holds — CRITICAL */}
      <div className="box" style={{ borderColor: separationHolds.length > 0 ? 'var(--risk)' : undefined }}>
        <div className="box__head">
          <div className="box__head-text">
            <p className="box__eyebrow">Critical</p>
            <h2 className="box__title">
              Separation Holds
              {separationHolds.length > 0 && (
                <span className="pill pill--risk" style={{ marginLeft: 'var(--sp-2)' }}>
                  <i></i>
                  {separationHolds.length}
                </span>
              )}
            </h2>
          </div>
        </div>
        <div className="box__body">
          {separationHolds.length > 0 ? (
            <DataTable
              columns={separationColumns}
              visibleColumns={separationCtrl.visibleColumns}
              data={separationCtrl.paged}
              search={separationCtrl.search}
              onSearchChange={separationCtrl.setSearch}
              sortKey={separationCtrl.sortKey}
              sortDir={separationCtrl.sortDir}
              onSort={separationCtrl.setSort}
              page={separationCtrl.page}
              totalPages={separationCtrl.totalPages}
              totalFiltered={separationCtrl.totalFiltered}
              pageSize={separationCtrl.pageSize}
              onPageChange={separationCtrl.setPage}
              onPageSizeChange={separationCtrl.setPageSize}
              activeFilters={separationCtrl.activeFilters}
              onFilter={separationCtrl.setFilter}
              onClearFilters={separationCtrl.clearFilters}
              timeRange={separationCtrl.timeRange}
              onTimeRangeChange={separationCtrl.setTimeRange}
              hiddenColumns={separationCtrl.hiddenColumns}
              onToggleColumn={separationCtrl.toggleColumn}
              selectedIds={separationCtrl.selectedIds}
              onToggleSelect={separationCtrl.toggleSelect}
              onSelectAll={separationCtrl.selectAll}
              onClearSelection={separationCtrl.clearSelection}
              savedViews={separationCtrl.savedViews}
              onSaveView={separationCtrl.saveView}
              onLoadView={separationCtrl.loadView}
              onDeleteView={separationCtrl.deleteView}
              onExportCSV={separationCtrl.exportCSV}
              onExportExcel={separationCtrl.exportExcel}
              searchPlaceholder="Search separation holds..."
              keyAccessor={(r) => r.id}
              emptyMessage="No separation holds. All assets have active owners."
            />
          ) : (
            <div className="state">
              <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
              <h3 className="state__title">No separation holds</h3>
              <p className="state__body">
                All assets have active owners. No disabled or terminated accounts with assigned assets.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Asset Inventory */}
      <div className="box">
        <div className="box__head">
          <div className="box__head-text">
            <p className="box__eyebrow">Inventory</p>
            <h2 className="box__title">Asset Inventory</h2>
          </div>
          <div className="box__head-actions">
            <span className="fact">{assets.length} asset{assets.length !== 1 ? 's' : ''}</span>
          </div>
        </div>
        <div className="box__body">
          {assets.length > 0 ? (
            <DataTable
              columns={assetColumns}
              visibleColumns={assetCtrl.visibleColumns}
              data={assetCtrl.paged}
              search={assetCtrl.search}
              onSearchChange={assetCtrl.setSearch}
              sortKey={assetCtrl.sortKey}
              sortDir={assetCtrl.sortDir}
              onSort={assetCtrl.setSort}
              page={assetCtrl.page}
              totalPages={assetCtrl.totalPages}
              totalFiltered={assetCtrl.totalFiltered}
              pageSize={assetCtrl.pageSize}
              onPageChange={assetCtrl.setPage}
              onPageSizeChange={assetCtrl.setPageSize}
              activeFilters={assetCtrl.activeFilters}
              onFilter={assetCtrl.setFilter}
              onClearFilters={assetCtrl.clearFilters}
              timeRange={assetCtrl.timeRange}
              onTimeRangeChange={assetCtrl.setTimeRange}
              hiddenColumns={assetCtrl.hiddenColumns}
              onToggleColumn={assetCtrl.toggleColumn}
              selectedIds={assetCtrl.selectedIds}
              onToggleSelect={assetCtrl.toggleSelect}
              onSelectAll={assetCtrl.selectAll}
              onClearSelection={assetCtrl.clearSelection}
              savedViews={assetCtrl.savedViews}
              onSaveView={assetCtrl.saveView}
              onLoadView={assetCtrl.loadView}
              onDeleteView={assetCtrl.deleteView}
              onExportCSV={assetCtrl.exportCSV}
              onExportExcel={assetCtrl.exportExcel}
              searchPlaceholder="Search assets by tag, serial, model, account..."
              keyAccessor={(r) => r.id || r.tag || r.asset_tag}
              emptyMessage="No asset data ingested. Connect a procurement or MDM source to populate."
            />
          ) : (
            <div className="state">
              <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              <h3 className="state__title">No asset data</h3>
              <p className="state__body">
                No asset data ingested yet. Connect a procurement or MDM source to populate the inventory.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Refresh Forecasting */}
      <div className="box">
        <div className="box__head">
          <div className="box__head-text">
            <p className="box__eyebrow">Lifecycle</p>
            <h2 className="box__title">Refresh Forecasting</h2>
          </div>
        </div>
        <div className="box__body">
          {/* Past EOL */}
          {pastEOL.length > 0 && (
            <div style={{ marginBottom: 'var(--sp-5)' }}>
              <h3 style={{ fontSize: 'var(--text-base)', fontWeight: 600, marginBottom: 'var(--sp-2)', fontFamily: 'var(--sans)', color: 'var(--ink)' }}>
                Past End-of-Life ({pastEOL.length})
              </h3>
              <DataTable
                columns={pastEOLColumns}
                visibleColumns={pastEOLCtrl.visibleColumns}
                data={pastEOLCtrl.paged}
                search={pastEOLCtrl.search}
                onSearchChange={pastEOLCtrl.setSearch}
                sortKey={pastEOLCtrl.sortKey}
                sortDir={pastEOLCtrl.sortDir}
                onSort={pastEOLCtrl.setSort}
                page={pastEOLCtrl.page}
                totalPages={pastEOLCtrl.totalPages}
                totalFiltered={pastEOLCtrl.totalFiltered}
                pageSize={pastEOLCtrl.pageSize}
                onPageChange={pastEOLCtrl.setPage}
                onPageSizeChange={pastEOLCtrl.setPageSize}
                activeFilters={pastEOLCtrl.activeFilters}
                onFilter={pastEOLCtrl.setFilter}
                onClearFilters={pastEOLCtrl.clearFilters}
                timeRange={pastEOLCtrl.timeRange}
                onTimeRangeChange={pastEOLCtrl.setTimeRange}
                hiddenColumns={pastEOLCtrl.hiddenColumns}
                onToggleColumn={pastEOLCtrl.toggleColumn}
                selectedIds={pastEOLCtrl.selectedIds}
                onToggleSelect={pastEOLCtrl.toggleSelect}
                onSelectAll={pastEOLCtrl.selectAll}
                onClearSelection={pastEOLCtrl.clearSelection}
                savedViews={pastEOLCtrl.savedViews}
                onSaveView={pastEOLCtrl.saveView}
                onLoadView={pastEOLCtrl.loadView}
                onDeleteView={pastEOLCtrl.deleteView}
                onExportCSV={pastEOLCtrl.exportCSV}
                onExportExcel={pastEOLCtrl.exportExcel}
                searchPlaceholder="Search past EOL assets..."
                keyAccessor={(r) => r.tag || r.serial}
              />
            </div>
          )}

          {/* Projected by FY */}
          {byFY.length > 0 && (
            <div>
              <h3 style={{ fontSize: 'var(--text-base)', fontWeight: 600, marginBottom: 'var(--sp-2)', fontFamily: 'var(--sans)', color: 'var(--ink)' }}>
                Projected Replacement Cost by Fiscal Year
              </h3>
              <DataTable
                columns={byFYColumns}
                visibleColumns={byFYCtrl.visibleColumns}
                data={byFYCtrl.paged}
                search={byFYCtrl.search}
                onSearchChange={byFYCtrl.setSearch}
                sortKey={byFYCtrl.sortKey}
                sortDir={byFYCtrl.sortDir}
                onSort={byFYCtrl.setSort}
                page={byFYCtrl.page}
                totalPages={byFYCtrl.totalPages}
                totalFiltered={byFYCtrl.totalFiltered}
                pageSize={byFYCtrl.pageSize}
                onPageChange={byFYCtrl.setPage}
                onPageSizeChange={byFYCtrl.setPageSize}
                activeFilters={byFYCtrl.activeFilters}
                onFilter={byFYCtrl.setFilter}
                onClearFilters={byFYCtrl.clearFilters}
                timeRange={byFYCtrl.timeRange}
                onTimeRangeChange={byFYCtrl.setTimeRange}
                hiddenColumns={byFYCtrl.hiddenColumns}
                onToggleColumn={byFYCtrl.toggleColumn}
                selectedIds={byFYCtrl.selectedIds}
                onToggleSelect={byFYCtrl.toggleSelect}
                onSelectAll={byFYCtrl.selectAll}
                onClearSelection={byFYCtrl.clearSelection}
                savedViews={byFYCtrl.savedViews}
                onSaveView={byFYCtrl.saveView}
                onLoadView={byFYCtrl.loadView}
                onDeleteView={byFYCtrl.deleteView}
                onExportCSV={byFYCtrl.exportCSV}
                onExportExcel={byFYCtrl.exportExcel}
                searchPlaceholder="Search fiscal year projections..."
                keyAccessor={(r) => r.fiscal_year}
              />
            </div>
          )}

          {pastEOL.length === 0 && byFY.length === 0 && (
            <div className="state">
              <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              <h3 className="state__title">No forecast data</h3>
              <p className="state__body">
                No refresh forecast data available. Provide EOL dates and replacement costs to enable forecasting.
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

/* ── discovery panel ─────────────────────────────────────── */

function DiscoveryPanel() {
  const [groupBy, setGroupBy] = useState<'all' | 'vendor' | 'vlan' | 'switch'>('all');

  const { data: discoveryData, isLoading } = useQuery({
    queryKey: ['discovery'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/discovery');
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
  });

  const { data: byVendorData } = useQuery({
    queryKey: ['discovery-by-vendor'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/discovery/by-vendor');
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
    enabled: groupBy === 'vendor',
  });

  const { data: byVlanData } = useQuery({
    queryKey: ['discovery-by-vlan'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/discovery/by-vlan');
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
    enabled: groupBy === 'vlan',
  });

  const { data: unclaimedData } = useQuery({
    queryKey: ['discovery-unclaimed'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/discovery/unclaimed');
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
  });

  const devices = discoveryData?.devices || [];
  const vendorGroups = byVendorData?.groups || [];
  const vlanGroups = byVlanData?.groups || [];
  const unclaimed = unclaimedData?.devices || [];

  // Compute switch rows from devices
  const switchRows = useMemo(() => {
    const switchMap = new Map<string, { switch_name: string; port: string; count: number; unclaimed: number }>();
    devices.forEach((d: any) => {
      const sw = d.switch_name || d.switch || 'Unknown';
      const port = d.switch_port || '—';
      const key = `${sw}::${port}`;
      const entry = switchMap.get(key) || { switch_name: sw, port, count: 0, unclaimed: 0 };
      entry.count++;
      if (!d.classification && !d.owner) entry.unclaimed++;
      switchMap.set(key, entry);
    });
    return Array.from(switchMap.values());
  }, [devices]);

  // --- All Devices columns & controls ---
  const deviceColumns: Column<any>[] = [
    { key: 'mac', label: 'MAC', render: (r) => <span className="text-mono text-sm">{r.mac}</span> },
    { key: 'oui_vendor', label: 'OUI Vendor', render: (r) => <span className="text-sm">{r.oui_vendor || '—'}</span> },
    { key: 'observed_ips', label: 'Observed IPs', render: (r) => (
      r.observed_ips?.length > 0 ? (
        r.observed_ips.map((ip: string, i: number) => (
          <span key={i}>
            <span className="text-mono text-sm">{ip}</span>
            {i < r.observed_ips.length - 1 && ', '}
          </span>
        ))
      ) : <span className="text-muted">—</span>
    ), accessor: (r) => r.observed_ips?.join(', ') },
    { key: 'vlan', label: 'VLAN', render: (r) => <span className="text-mono text-sm">{r.vlan || '—'}</span> },
    { key: 'switch_port', label: 'Switch Port', render: (r) => <span className="text-mono text-sm">{r.switch_port || '—'}</span> },
    { key: 'first_seen_at', label: 'First Seen', dateField: true, render: (r) => <span className="text-sm text-muted">{r.first_seen_at ? new Date(r.first_seen_at).toLocaleDateString() : '—'}</span> },
    { key: 'last_seen_at', label: 'Last Seen', dateField: true, render: (r) => r.last_seen_at ? <StalenessIndicator lastSeenAt={r.last_seen_at} /> : <span className="text-muted">—</span> },
    { key: 'classification', label: 'Classification', render: (r) => r.classification ? (
      <span className="pill pill--ok"><i></i>{r.classification}</span>
    ) : (
      <span className="pill pill--warn"><i></i>unclaimed</span>
    )},
  ];
  const deviceCtrl = useTableControls(devices, deviceColumns, { storageKey: 'discovery-devices' });

  // --- Vendor group columns & controls ---
  const vendorColumns: Column<any>[] = [
    { key: 'vendor', label: 'Vendor (OUI)', render: (r) => <span style={{ fontWeight: 500 }}>{r.vendor}</span> },
    { key: 'device_count', label: 'Device Count', render: (r) => <span className="text-mono">{r.device_count}</span> },
    { key: 'unique_vlans', label: 'Unique VLANs', render: (r) => <span className="text-mono">{r.unique_vlans ?? '—'}</span> },
    { key: 'unclaimed_count', label: 'Unclaimed', render: (r) => r.unclaimed_count > 0 ? (
      <span className="pill pill--risk"><i></i>{r.unclaimed_count}</span>
    ) : (
      <span className="text-muted">0</span>
    )},
  ];
  const vendorCtrl = useTableControls(vendorGroups, vendorColumns, { storageKey: 'discovery-by-vendor' });

  // --- VLAN group columns & controls ---
  const vlanColumns: Column<any>[] = [
    { key: 'vlan', label: 'VLAN', render: (r) => <span className="text-mono" style={{ fontWeight: 500 }}>{r.vlan}</span> },
    { key: 'device_count', label: 'Device Count', render: (r) => <span className="text-mono">{r.device_count}</span> },
    { key: 'unique_vendors', label: 'Unique Vendors', render: (r) => <span className="text-mono">{r.unique_vendors ?? '—'}</span> },
    { key: 'unclaimed_count', label: 'Unclaimed', render: (r) => r.unclaimed_count > 0 ? (
      <span className="pill pill--risk"><i></i>{r.unclaimed_count}</span>
    ) : (
      <span className="text-muted">0</span>
    )},
  ];
  const vlanCtrl = useTableControls(vlanGroups, vlanColumns, { storageKey: 'discovery-by-vlan' });

  // --- Switch group columns & controls ---
  const switchColumns: Column<any>[] = [
    { key: 'switch_name', label: 'Switch', render: (r) => <span className="text-mono" style={{ fontWeight: 500 }}>{r.switch_name}</span> },
    { key: 'port', label: 'Port', render: (r) => <span className="text-mono text-sm">{r.port}</span> },
    { key: 'count', label: 'Device Count', render: (r) => <span className="text-mono">{r.count}</span> },
    { key: 'unclaimed', label: 'Unclaimed', render: (r) => r.unclaimed > 0 ? (
      <span className="pill pill--risk"><i></i>{r.unclaimed}</span>
    ) : (
      <span className="text-muted">0</span>
    )},
  ];
  const switchCtrl = useTableControls(switchRows, switchColumns, { storageKey: 'discovery-by-switch' });

  // --- Unclaimed devices columns & controls ---
  const unclaimedColumns: Column<any>[] = [
    { key: 'mac', label: 'MAC', render: (r) => <span className="text-mono text-sm">{r.mac}</span> },
    { key: 'oui_vendor', label: 'OUI Vendor', render: (r) => <span className="text-sm">{r.oui_vendor || '—'}</span> },
    { key: 'observed_ips', label: 'IPs', render: (r) => <span className="text-mono text-sm">{r.observed_ips?.join(', ') || '—'}</span>, accessor: (r) => r.observed_ips?.join(', ') },
    { key: 'vlan', label: 'VLAN', render: (r) => <span className="text-mono text-sm">{r.vlan || '—'}</span> },
    { key: 'last_seen_at', label: 'Last Seen', dateField: true, render: (r) => r.last_seen_at ? <StalenessIndicator lastSeenAt={r.last_seen_at} /> : <span className="text-muted">—</span> },
    { key: 'action', label: 'Action', sortable: false, render: () => (
      <button className="btn btn--sm">Claim →</button>
    )},
  ];
  const unclaimedCtrl = useTableControls(unclaimed, unclaimedColumns, { storageKey: 'discovery-unclaimed' });

  if (isLoading) {
    return <div className="page-loading" role="status" aria-live="polite">Loading discovery data…</div>;
  }

  return (
    <>
      {/* Passive-only notice */}
      <div className="notice notice--warn">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="16" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
        <div>
          <p className="notice__title">Discovery is passive</p>
          <p className="notice__body">
            ATLAS does not scan, probe, or interrogate devices. Data derives from NAC, netflow, ARP, and DHCP records supplied by your network team.
            {devices.length > 0 && <> <strong>{devices.length}</strong> devices observed{unclaimed.length > 0 && <>, <strong style={{ color: 'var(--risk)' }}>{unclaimed.length}</strong> unclaimed</>}.</>}
          </p>
        </div>
      </div>

      {/* Grouping Tabs */}
      <div className="tabs" role="tablist" aria-label="Discovery grouping">
        <button
          className="tab"
          role="tab"
          aria-selected={groupBy === 'all'}
          onClick={() => setGroupBy('all')}
        >
          All Devices ({devices.length})
        </button>
        <button
          className="tab"
          role="tab"
          aria-selected={groupBy === 'vendor'}
          onClick={() => setGroupBy('vendor')}
        >
          By Vendor
        </button>
        <button
          className="tab"
          role="tab"
          aria-selected={groupBy === 'vlan'}
          onClick={() => setGroupBy('vlan')}
        >
          By VLAN
        </button>
        <button
          className="tab"
          role="tab"
          aria-selected={groupBy === 'switch'}
          onClick={() => setGroupBy('switch')}
        >
          By Switch
        </button>
      </div>

      {/* All Devices view */}
      {groupBy === 'all' && (
        <div className="box">
          <div className="box__head">
            <div className="box__head-text">
              <p className="box__eyebrow">Discovered</p>
              <h2 className="box__title">Devices</h2>
            </div>
            <div className="box__head-actions">
              <span className="fact">{devices.length} devices</span>
            </div>
          </div>
          <div className="box__body box__body--flush">
            {devices.length > 0 ? (
              <DataTable
                columns={deviceColumns}
                visibleColumns={deviceCtrl.visibleColumns}
                data={deviceCtrl.paged}
                search={deviceCtrl.search}
                onSearchChange={deviceCtrl.setSearch}
                sortKey={deviceCtrl.sortKey}
                sortDir={deviceCtrl.sortDir}
                onSort={deviceCtrl.setSort}
                page={deviceCtrl.page}
                totalPages={deviceCtrl.totalPages}
                totalFiltered={deviceCtrl.totalFiltered}
                pageSize={deviceCtrl.pageSize}
                onPageChange={deviceCtrl.setPage}
                onPageSizeChange={deviceCtrl.setPageSize}
                activeFilters={deviceCtrl.activeFilters}
                onFilter={deviceCtrl.setFilter}
                onClearFilters={deviceCtrl.clearFilters}
                timeRange={deviceCtrl.timeRange}
                onTimeRangeChange={deviceCtrl.setTimeRange}
                hiddenColumns={deviceCtrl.hiddenColumns}
                onToggleColumn={deviceCtrl.toggleColumn}
                selectedIds={deviceCtrl.selectedIds}
                onToggleSelect={deviceCtrl.toggleSelect}
                onSelectAll={deviceCtrl.selectAll}
                onClearSelection={deviceCtrl.clearSelection}
                savedViews={deviceCtrl.savedViews}
                onSaveView={deviceCtrl.saveView}
                onLoadView={deviceCtrl.loadView}
                onDeleteView={deviceCtrl.deleteView}
                onExportCSV={deviceCtrl.exportCSV}
                onExportExcel={deviceCtrl.exportExcel}
                searchPlaceholder="Search by MAC, vendor, IP, VLAN..."
                keyAccessor={(r) => r.mac}
                emptyMessage="No devices discovered. Ingest NAC, netflow, ARP, or DHCP records to populate."
              />
            ) : (
              <div className="state">
                <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="16" x2="12" y2="12" />
                  <line x1="12" y1="8" x2="12.01" y2="8" />
                </svg>
                <h3 className="state__title">No devices discovered</h3>
                <p className="state__body">
                  Ingest NAC, netflow, ARP, or DHCP records to populate discovery data.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Vendor view */}
      {groupBy === 'vendor' && (
        <div className="box">
          <div className="box__head">
            <div className="box__head-text">
              <p className="box__eyebrow">Grouped</p>
              <h2 className="box__title">By Vendor</h2>
            </div>
          </div>
          <div className="box__body box__body--flush">
            {vendorGroups.length > 0 ? (
              <DataTable
                columns={vendorColumns}
                visibleColumns={vendorCtrl.visibleColumns}
                data={vendorCtrl.paged}
                search={vendorCtrl.search}
                onSearchChange={vendorCtrl.setSearch}
                sortKey={vendorCtrl.sortKey}
                sortDir={vendorCtrl.sortDir}
                onSort={vendorCtrl.setSort}
                page={vendorCtrl.page}
                totalPages={vendorCtrl.totalPages}
                totalFiltered={vendorCtrl.totalFiltered}
                pageSize={vendorCtrl.pageSize}
                onPageChange={vendorCtrl.setPage}
                onPageSizeChange={vendorCtrl.setPageSize}
                activeFilters={vendorCtrl.activeFilters}
                onFilter={vendorCtrl.setFilter}
                onClearFilters={vendorCtrl.clearFilters}
                timeRange={vendorCtrl.timeRange}
                onTimeRangeChange={vendorCtrl.setTimeRange}
                hiddenColumns={vendorCtrl.hiddenColumns}
                onToggleColumn={vendorCtrl.toggleColumn}
                selectedIds={vendorCtrl.selectedIds}
                onToggleSelect={vendorCtrl.toggleSelect}
                onSelectAll={vendorCtrl.selectAll}
                onClearSelection={vendorCtrl.clearSelection}
                savedViews={vendorCtrl.savedViews}
                onSaveView={vendorCtrl.saveView}
                onLoadView={vendorCtrl.loadView}
                onDeleteView={vendorCtrl.deleteView}
                onExportCSV={vendorCtrl.exportCSV}
                onExportExcel={vendorCtrl.exportExcel}
                searchPlaceholder="Search vendors..."
                keyAccessor={(r) => r.vendor}
              />
            ) : (
              <div className="state">
                <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="16" x2="12" y2="12" />
                  <line x1="12" y1="8" x2="12.01" y2="8" />
                </svg>
                <h3 className="state__title">No vendor data</h3>
                <p className="state__body">
                  No vendor grouping data available. Ensure OUI data is being ingested.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* VLAN view */}
      {groupBy === 'vlan' && (
        <div className="box">
          <div className="box__head">
            <div className="box__head-text">
              <p className="box__eyebrow">Grouped</p>
              <h2 className="box__title">By VLAN</h2>
            </div>
          </div>
          <div className="box__body box__body--flush">
            {vlanGroups.length > 0 ? (
              <DataTable
                columns={vlanColumns}
                visibleColumns={vlanCtrl.visibleColumns}
                data={vlanCtrl.paged}
                search={vlanCtrl.search}
                onSearchChange={vlanCtrl.setSearch}
                sortKey={vlanCtrl.sortKey}
                sortDir={vlanCtrl.sortDir}
                onSort={vlanCtrl.setSort}
                page={vlanCtrl.page}
                totalPages={vlanCtrl.totalPages}
                totalFiltered={vlanCtrl.totalFiltered}
                pageSize={vlanCtrl.pageSize}
                onPageChange={vlanCtrl.setPage}
                onPageSizeChange={vlanCtrl.setPageSize}
                activeFilters={vlanCtrl.activeFilters}
                onFilter={vlanCtrl.setFilter}
                onClearFilters={vlanCtrl.clearFilters}
                timeRange={vlanCtrl.timeRange}
                onTimeRangeChange={vlanCtrl.setTimeRange}
                hiddenColumns={vlanCtrl.hiddenColumns}
                onToggleColumn={vlanCtrl.toggleColumn}
                selectedIds={vlanCtrl.selectedIds}
                onToggleSelect={vlanCtrl.toggleSelect}
                onSelectAll={vlanCtrl.selectAll}
                onClearSelection={vlanCtrl.clearSelection}
                savedViews={vlanCtrl.savedViews}
                onSaveView={vlanCtrl.saveView}
                onLoadView={vlanCtrl.loadView}
                onDeleteView={vlanCtrl.deleteView}
                onExportCSV={vlanCtrl.exportCSV}
                onExportExcel={vlanCtrl.exportExcel}
                searchPlaceholder="Search VLANs..."
                keyAccessor={(r) => r.vlan}
              />
            ) : (
              <div className="state">
                <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="16" x2="12" y2="12" />
                  <line x1="12" y1="8" x2="12.01" y2="8" />
                </svg>
                <h3 className="state__title">No VLAN data</h3>
                <p className="state__body">
                  No VLAN grouping data available. Ensure VLAN information is being ingested.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Switch view */}
      {groupBy === 'switch' && (
        <div className="box">
          <div className="box__head">
            <div className="box__head-text">
              <p className="box__eyebrow">Grouped</p>
              <h2 className="box__title">By Switch</h2>
            </div>
          </div>
          <div className="box__body box__body--flush">
            {devices.length > 0 ? (
              <DataTable
                columns={switchColumns}
                visibleColumns={switchCtrl.visibleColumns}
                data={switchCtrl.paged}
                search={switchCtrl.search}
                onSearchChange={switchCtrl.setSearch}
                sortKey={switchCtrl.sortKey}
                sortDir={switchCtrl.sortDir}
                onSort={switchCtrl.setSort}
                page={switchCtrl.page}
                totalPages={switchCtrl.totalPages}
                totalFiltered={switchCtrl.totalFiltered}
                pageSize={switchCtrl.pageSize}
                onPageChange={switchCtrl.setPage}
                onPageSizeChange={switchCtrl.setPageSize}
                activeFilters={switchCtrl.activeFilters}
                onFilter={switchCtrl.setFilter}
                onClearFilters={switchCtrl.clearFilters}
                timeRange={switchCtrl.timeRange}
                onTimeRangeChange={switchCtrl.setTimeRange}
                hiddenColumns={switchCtrl.hiddenColumns}
                onToggleColumn={switchCtrl.toggleColumn}
                selectedIds={switchCtrl.selectedIds}
                onToggleSelect={switchCtrl.toggleSelect}
                onSelectAll={switchCtrl.selectAll}
                onClearSelection={switchCtrl.clearSelection}
                savedViews={switchCtrl.savedViews}
                onSaveView={switchCtrl.saveView}
                onLoadView={switchCtrl.loadView}
                onDeleteView={switchCtrl.deleteView}
                onExportCSV={switchCtrl.exportCSV}
                onExportExcel={switchCtrl.exportExcel}
                searchPlaceholder="Search switches..."
                keyAccessor={(r) => `${r.switch_name}-${r.port}`}
                emptyMessage="No switch data available."
              />
            ) : (
              <div className="state">
                <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="16" x2="12" y2="12" />
                  <line x1="12" y1="8" x2="12.01" y2="8" />
                </svg>
                <h3 className="state__title">No switch data</h3>
                <p className="state__body">
                  No devices with switch information have been discovered yet.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Unclaimed Devices */}
      <div className="box">
        <div className="box__head">
          <div className="box__head-text">
            <p className="box__eyebrow">Action Required</p>
            <h2 className="box__title">Unclaimed Devices</h2>
          </div>
          <div className="box__head-actions">
            {unclaimed.length > 0 && (
              <span className="pill pill--risk"><i></i>{unclaimed.length}</span>
            )}
            <span className="fact">network-observed, unclaimed</span>
          </div>
        </div>
        <div className="box__body box__body--flush">
          {unclaimed.length > 0 ? (
            <DataTable
              columns={unclaimedColumns}
              visibleColumns={unclaimedCtrl.visibleColumns}
              data={unclaimedCtrl.paged}
              search={unclaimedCtrl.search}
              onSearchChange={unclaimedCtrl.setSearch}
              sortKey={unclaimedCtrl.sortKey}
              sortDir={unclaimedCtrl.sortDir}
              onSort={unclaimedCtrl.setSort}
              page={unclaimedCtrl.page}
              totalPages={unclaimedCtrl.totalPages}
              totalFiltered={unclaimedCtrl.totalFiltered}
              pageSize={unclaimedCtrl.pageSize}
              onPageChange={unclaimedCtrl.setPage}
              onPageSizeChange={unclaimedCtrl.setPageSize}
              activeFilters={unclaimedCtrl.activeFilters}
              onFilter={unclaimedCtrl.setFilter}
              onClearFilters={unclaimedCtrl.clearFilters}
              timeRange={unclaimedCtrl.timeRange}
              onTimeRangeChange={unclaimedCtrl.setTimeRange}
              hiddenColumns={unclaimedCtrl.hiddenColumns}
              onToggleColumn={unclaimedCtrl.toggleColumn}
              selectedIds={unclaimedCtrl.selectedIds}
              onToggleSelect={unclaimedCtrl.toggleSelect}
              onSelectAll={unclaimedCtrl.selectAll}
              onClearSelection={unclaimedCtrl.clearSelection}
              savedViews={unclaimedCtrl.savedViews}
              onSaveView={unclaimedCtrl.saveView}
              onLoadView={unclaimedCtrl.loadView}
              onDeleteView={unclaimedCtrl.deleteView}
              onExportCSV={unclaimedCtrl.exportCSV}
              onExportExcel={unclaimedCtrl.exportExcel}
              searchPlaceholder="Search unclaimed devices..."
              keyAccessor={(r) => r.mac}
              emptyMessage="All discovered devices are claimed. No action required."
            />
          ) : (
            <div className="state">
              <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
              <h3 className="state__title">All devices claimed</h3>
              <p className="state__body">
                All discovered devices are claimed. No action required.
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

/* ── page ────────────────────────────────────────────────── */

type AssetsTab = 'assets' | 'discovery';

function initialTab(): AssetsTab {
  try {
    return new URLSearchParams(window.location.search).get('tab') === 'discovery' ? 'discovery' : 'assets';
  } catch {
    return 'assets';
  }
}

export function AssetsPage() {
  const [tab, setTab] = useState<AssetsTab>(initialTab);

  return (
    <>
      <div className="pagehead">
        <div className="pagehead__row">
          <div className="pagehead__text">
            <p className="pagehead__eyebrow">IT Inventory</p>
            <h1 className="pagehead__title">{tab === 'assets' ? 'Asset Accountability' : 'Network Discovery'}</h1>
            <div className="pagehead__facts">
              <span className="fact">
                {tab === 'assets'
                  ? 'Hardware lifecycle & compliance'
                  : 'Passive observation of devices seen on the network'}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="pagebody">
        <div className="tabs" role="tablist" aria-label="Asset inventory view">
          <button
            className="tab"
            role="tab"
            aria-selected={tab === 'assets'}
            onClick={() => setTab('assets')}
          >
            Assets
          </button>
          <button
            className="tab"
            role="tab"
            aria-selected={tab === 'discovery'}
            onClick={() => setTab('discovery')}
          >
            Discovery
          </button>
        </div>

        {tab === 'assets' ? <AssetsPanel /> : <DiscoveryPanel />}
      </div>
    </>
  );
}
